/** Procedural ambient music — Web Audio synthesis, no audio assets.
 *
 *  Four looping "tracks" (map day/night, building day/night) defined as chord
 *  progressions + instrument settings; a single lookahead scheduler renders the
 *  active one with soft synth pads, plucked keys, a sub bass and a vinyl-noise
 *  bed. Scene changes duck the master and swap patterns at the next bar, so
 *  transitions read as a DJ-style crossfade without running four synths at once.
 *
 *  Browsers block audio until a user gesture: call initMusicOnGesture() once —
 *  it arms a one-shot pointerdown/keydown listener that boots the AudioContext.
 */
import { writable } from 'svelte/store';

export type MusicScene = 'map-day' | 'map-night' | 'bld-day' | 'bld-night';

const LS_KEY = 'cloud-nine-music';
const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
export const musicOn = writable(stored !== '0');
musicOn.subscribe((on) => {
  try {
    localStorage.setItem(LS_KEY, on ? '1' : '0');
  } catch {
    /* private mode */
  }
});

interface TrackDef {
  /** chords as midi note arrays (pad voicing); bass takes the first note -12 */
  chords: number[][];
  barSec: number; // seconds per chord
  padCut: number; // pad lowpass Hz
  padLevel: number;
  pluckChance: number; // per 8th-note chance of a plucked chord tone
  pluckLevel: number;
  bellEvery: number; // a high sparkle every N bars (0 = never)
  noiseLevel: number; // vinyl-bed level
  bassLevel: number;
}

// Voicings stay in a mellow mid register; map = warmer/major-lean, building = smaller/moodier.
const TRACKS: Record<MusicScene, TrackDef> = {
  // "Sunset Drive" — warm maj7 loop for the golden-hour diorama
  'map-day': {
    chords: [
      [53, 57, 60, 64], // Fmaj7
      [48, 55, 60, 64], // C/E-ish
      [45, 52, 57, 60], // Am7
      [50, 55, 59, 62], // G6/D
    ],
    barSec: 4.6,
    padCut: 1050,
    padLevel: 0.5,
    pluckChance: 0.42,
    pluckLevel: 0.5,
    bellEvery: 0,
    noiseLevel: 0.5,
    bassLevel: 0.55,
  },
  // "Neon Rain" — darker minor-9 wash, sparse sparkles
  'map-night': {
    chords: [
      [45, 52, 55, 59], // Am9 (no root dbl)
      [41, 48, 53, 57], // Fmaj7
      [43, 50, 55, 59], // G add
      [45, 52, 57, 60], // Am7
    ],
    barSec: 6.0,
    padCut: 720,
    padLevel: 0.55,
    pluckChance: 0.2,
    pluckLevel: 0.4,
    bellEvery: 2,
    noiseLevel: 0.8,
    bassLevel: 0.5,
  },
  // "The Hustle" — lo-fi ii-V loop while the crew works
  'bld-day': {
    chords: [
      [50, 57, 60, 65], // Dm7
      [43, 53, 59, 62], // G9
      [48, 55, 59, 64], // Cmaj7
      [45, 52, 60, 64], // Am7
    ],
    barSec: 4.0,
    padCut: 900,
    padLevel: 0.4,
    pluckChance: 0.6,
    pluckLevel: 0.6,
    bellEvery: 0,
    noiseLevel: 1.0,
    bassLevel: 0.65,
  },
  // "Afterhours" — slow basement drones, the quietest of the four
  'bld-night': {
    chords: [
      [45, 52, 57], // Am
      [41, 48, 53], // F
      [43, 50, 55], // G
      [40, 47, 52], // Em
    ],
    barSec: 7.2,
    padCut: 560,
    padLevel: 0.55,
    pluckChance: 0.1,
    pluckLevel: 0.35,
    bellEvery: 3,
    noiseLevel: 0.7,
    bassLevel: 0.45,
  },
};

const midiHz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

class MusicEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private duck!: GainNode; // scene-transition / visibility ducking
  private delay!: DelayNode;
  private noiseGain!: GainNode;
  private scene: MusicScene = 'map-day';
  private pendingScene: MusicScene | null = null;
  private bar = 0;
  private nextBarAt = 0;
  private on = true;
  private level = 0.14;
  private rainGain: GainNode | null = null;
  private rainOn = false;

  constructor() {
    musicOn.subscribe((on) => {
      this.on = on;
      if (this.ctx) this.master.gain.linearRampToValueAtTime(on ? this.level : 0, this.ctx.currentTime + 0.4);
    });
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!this.ctx) return;
        const t = this.ctx.currentTime;
        this.duck.gain.linearRampToValueAtTime(document.hidden ? 0 : 1, t + 0.3);
      });
    }
  }

  /** Create the context + graph. Must be called from a user gesture. */
  start() {
    if (this.ctx) return;
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.on ? this.level : 0;
    this.duck = ctx.createGain();
    this.duck.gain.value = 1;
    // gentle master lowpass keeps everything behind the UI, never shrill
    const soften = ctx.createBiquadFilter();
    soften.type = 'lowpass';
    soften.frequency.value = 4200;
    this.duck.connect(soften).connect(this.master).connect(ctx.destination);

    // shared echo for plucks/bells
    this.delay = ctx.createDelay(1.2);
    this.delay.delayTime.value = 0.42;
    const fb = ctx.createGain();
    fb.gain.value = 0.32;
    const dampen = ctx.createBiquadFilter();
    dampen.type = 'lowpass';
    dampen.frequency.value = 1600;
    this.delay.connect(dampen).connect(fb).connect(this.delay);
    const wet = ctx.createGain();
    wet.gain.value = 0.35;
    this.delay.connect(wet).connect(this.duck);

    // looping vinyl-noise bed
    const len = 2 * ctx.sampleRate;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (Math.random() < 0.0004 ? 6 : 1); // rare crackle
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    noise.loop = true;
    const nFilt = ctx.createBiquadFilter();
    nFilt.type = 'bandpass';
    nFilt.frequency.value = 2600;
    nFilt.Q.value = 0.4;
    this.noiseGain = ctx.createGain();
    this.noiseGain.gain.value = 0.008;
    noise.connect(nFilt).connect(this.noiseGain).connect(this.duck);
    noise.start();

    // rain bed (own loop of the same noise buffer, darker filter), silent until setRain(true)
    const rainSrc = ctx.createBufferSource();
    rainSrc.buffer = buf;
    rainSrc.loop = true;
    rainSrc.playbackRate.value = 0.86;
    const rFilt = ctx.createBiquadFilter();
    rFilt.type = 'lowpass';
    rFilt.frequency.value = 900;
    this.rainGain = ctx.createGain();
    this.rainGain.gain.value = this.rainOn ? 0.05 : 0;
    rainSrc.connect(rFilt).connect(this.rainGain).connect(this.duck);
    rainSrc.start();

    this.nextBarAt = ctx.currentTime + 0.15;
    setInterval(() => this.tick(), 250); // runs for the app's lifetime (engine is a singleton)
  }

  /** Fade the rain-noise bed in/out (driven by ui-sim/weather.ts). */
  setRain(on: boolean) {
    if (on === this.rainOn) return;
    this.rainOn = on;
    if (this.ctx && this.rainGain) {
      this.rainGain.gain.linearRampToValueAtTime(on ? 0.05 : 0, this.ctx.currentTime + 3);
    }
  }

  // ---- one-shot SFX (interior juice; share the master/mute chain) --------
  /** Elevator arrival: soft two-note ding. */
  ding() {
    if (!this.ctx || !this.on) return;
    const t = this.ctx.currentTime;
    this.tone(1318.5, t, 0.35, 0.035, 'sine');
    this.tone(1760, t + 0.13, 0.45, 0.028, 'sine');
  }

  /** Deal Spot sale: register blip + coin ring. */
  chaChing() {
    if (!this.ctx || !this.on) return;
    const t = this.ctx.currentTime;
    this.tone(2093, t, 0.1, 0.03, 'square');
    this.tone(2637, t + 0.07, 0.5, 0.03, 'triangle');
  }

  /** Construction: three muffled hammer knocks. */
  hammer() {
    if (!this.ctx || !this.on) return;
    const t = this.ctx.currentTime;
    for (const dt of [0, 0.28, 0.56]) this.thump(t + 0.1 + dt);
  }

  private tone(hz: number, t: number, dur: number, level: number, type: OscillatorType) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, t);
    g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    o.connect(g).connect(this.duck);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private thump(t: number) {
    const ctx = this.ctx!;
    const len = Math.floor(0.08 * ctx.sampleRate);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 220;
    const g = ctx.createGain();
    g.gain.value = 0.12;
    src.connect(f).connect(g).connect(this.duck);
    src.start(t);
  }

  setScene(scene: MusicScene) {
    if (scene === this.scene && !this.pendingScene) return;
    if (!this.ctx) {
      this.scene = scene; // remember for when audio boots
      return;
    }
    if (scene === this.scene) {
      this.pendingScene = null;
      return;
    }
    this.pendingScene = scene;
    // duck toward the swap, restore after — reads as a crossfade
    const t = this.ctx.currentTime;
    this.duck.gain.cancelScheduledValues(t);
    this.duck.gain.setValueAtTime(this.duck.gain.value, t);
    this.duck.gain.linearRampToValueAtTime(0.35, t + 1.2);
  }

  /** Lookahead scheduler: renders the next bar shortly before it starts. */
  private tick() {
    const ctx = this.ctx!;
    if (ctx.state === 'suspended') void ctx.resume();
    while (this.nextBarAt < ctx.currentTime + 1.2) {
      if (this.pendingScene) {
        this.scene = this.pendingScene;
        this.pendingScene = null;
        this.bar = 0;
        const t = ctx.currentTime;
        this.duck.gain.cancelScheduledValues(t);
        this.duck.gain.setValueAtTime(this.duck.gain.value, t);
        this.duck.gain.linearRampToValueAtTime(document.hidden ? 0 : 1, t + 2.0);
      }
      const trk = TRACKS[this.scene];
      this.renderBar(trk, this.nextBarAt);
      this.noiseGain.gain.linearRampToValueAtTime(0.008 * trk.noiseLevel, this.nextBarAt + 1);
      this.nextBarAt += trk.barSec;
      this.bar++;
    }
  }

  private renderBar(trk: TrackDef, t0: number) {
    const chord = trk.chords[this.bar % trk.chords.length];
    const dur = trk.barSec;
    for (const m of chord) this.pad(midiHz(m), t0, dur, trk.padLevel / chord.length, trk.padCut);
    this.bass(midiHz(chord[0] - 12), t0, dur, trk.bassLevel);
    // plucked chord tones on a loose 8th grid (skip beat 1 — the pad carries it)
    const steps = Math.max(4, Math.round(dur / 0.55));
    for (let s = 1; s < steps; s++) {
      if (Math.random() < trk.pluckChance) {
        const m = chord[1 + Math.floor(Math.random() * (chord.length - 1))] + (Math.random() < 0.25 ? 12 : 0);
        this.pluck(midiHz(m), t0 + (s * dur) / steps + Math.random() * 0.03, trk.pluckLevel);
      }
    }
    if (trk.bellEvery > 0 && this.bar % trk.bellEvery === 1) {
      this.bell(midiHz(chord[chord.length - 1] + 24), t0 + dur * (0.4 + Math.random() * 0.3));
    }
  }

  // ---- voices ------------------------------------------------------------
  private pad(hz: number, t: number, dur: number, level: number, cut: number) {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(level, t + dur * 0.35);
    g.gain.setValueAtTime(level, t + dur * 0.75);
    g.gain.linearRampToValueAtTime(0, t + dur * 1.25); // overlaps the next chord
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cut;
    g.connect(f).connect(this.duck);
    for (const det of [-6, 5]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = hz;
      o.detune.value = det;
      const og = ctx.createGain();
      og.gain.value = 0.5;
      o.connect(og).connect(g);
      o.start(t);
      o.stop(t + dur * 1.3);
    }
    const sin = ctx.createOscillator();
    sin.type = 'sine';
    sin.frequency.value = hz;
    sin.connect(g);
    sin.start(t);
    sin.stop(t + dur * 1.3);
  }

  private pluck(hz: number, t: number, level: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = hz;
    const g = ctx.createGain();
    const peak = 0.16 * level;
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1900;
    o.connect(f).connect(g);
    g.connect(this.duck);
    g.connect(this.delay);
    o.start(t);
    o.stop(t + 1);
  }

  private bass(hz: number, t: number, dur: number, level: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.2 * level, t + 0.25);
    g.gain.setValueAtTime(0.2 * level, t + dur * 0.7);
    g.gain.linearRampToValueAtTime(0, t + dur);
    o.connect(g).connect(this.duck);
    o.start(t);
    o.stop(t + dur + 0.1);
  }

  private bell(hz: number, t: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.05, t);
    g.gain.exponentialRampToValueAtTime(0.0005, t + 2.4);
    o.connect(g);
    g.connect(this.duck);
    g.connect(this.delay);
    o.start(t);
    o.stop(t + 2.5);
  }
}

export const music = new MusicEngine();

/** Arm a one-shot listener that boots audio on the first user gesture. */
export function initMusicOnGesture() {
  const boot = () => {
    music.start();
    window.removeEventListener('pointerdown', boot);
    window.removeEventListener('keydown', boot);
  };
  window.addEventListener('pointerdown', boot);
  window.addEventListener('keydown', boot);
}
